import { useEffect, useState } from "react";
import { MAX_COMPARE } from "../../shared/compare";

// The comparison tray. Per-browser convenience only — never server state, so
// localStorage is the right home for it, and it must degrade silently when
// storage is unavailable (private mode, blocked site data).

const KEY = "cc-compare";
const isBrowser = typeof window !== "undefined";
const listeners = new Set<(ids: string[]) => void>();

function read(): string[] {
  if (!isBrowser) return [];
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as string[]).slice(0, MAX_COMPARE) : [];
  } catch {
    return [];
  }
}

function write(ids: string[]) {
  if (!isBrowser) return;
  try {
    localStorage.setItem(KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable — the tray simply does not persist.
  }
  listeners.forEach((fn) => fn(ids));
}

export function useCompare() {
  // Always start empty so the server render and the first client render agree;
  // the stored selection is adopted in an effect, after hydration.
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    setIds(read());
    const fn = (next: string[]) => setIds(next);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  return {
    ids,
    has: (id: string) => ids.includes(id),
    full: ids.length >= MAX_COMPARE,
    toggle: (id: string) => {
      const next = ids.includes(id)
        ? ids.filter((x) => x !== id)
        : ids.length >= MAX_COMPARE
          ? ids
          : [...ids, id];
      write(next);
    },
    clear: () => write([]),
  };
}
